/**
 * DashboardTransactionList - Compact recent transactions panel.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { EmptyState } from '@/components/v2/ui';
import { EID } from '@/lib/element-ids.registry';

interface Transaction {
  id: string;
  created_at: string;
  description: string;
  amount: number;
}

interface DashboardTransactionListProps {
  transactions: Transaction[];
}

export function DashboardTransactionList({ transactions }: DashboardTransactionListProps) {
  const { t } = useTranslation();
  return (
    <div id={EID['dashboard-transactions-section']} className="dashboard-transactions rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          {t('dashboard.recentTransactions')}
        </h3>
        {transactions.length > 0 && (
          <Link
            id={EID['dashboard-transactions-all-link']}
            to="/v2/credits"
            className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
          >
            {t('dashboard.allTransactions')}
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      {transactions.length > 0 ? (
        <div className="space-y-2.5">
          {transactions.map((tx) => {
            const isPositive = tx.amount > 0;
            const Icon = isPositive ? ArrowDownLeft : ArrowUpRight;
            return (
              <div
                key={tx.id}
                className="flex items-center gap-3"
              >
                <div className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg ${isPositive ? 'bg-emerald-100/60 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400' : 'bg-red-100/60 dark:bg-red-950/40 text-red-500 dark:text-red-400'}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">
                    {tx.description}
                  </p>
                  <p className="text-2xs text-neutral-400 dark:text-neutral-600">
                    {tx.created_at}
                  </p>
                </div>
                <span className={`text-xs font-mono ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                  {isPositive ? '+' : ''}{tx.amount.toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={t('dashboard.noTransactions')}
          description={t('billing.noActivity')}
          size="sm"
        />
      )}
    </div>
  );
}
